/**
 * media-gallery.js — FIXED EDITION v2
 *
 * KEY FIXES vs original Dawn:
 * ① _viewerReady race replaced with Promise + retry queue.
 *    Eliminates timing edge case where setActiveMedia was called
 *    before the custom element was upgraded.
 *
 * ② setActiveMedia: removed scrollIntoView that fought with
 *    product-info.js scroll-restore (caused page jump on swatch
 *    change). Now only scrolls when genuinely out of viewport.
 *
 * ③ setActiveThumbnail: null-safe, no throw on missing elements.
 *
 * ④ Debounce helper included locally (no global Dawn dependency).
 *
 * ⑤ slideChanged debounce 50ms → 30ms for snappier thumbnail sync.
 */

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {

      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer:     this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this._controller = new AbortController();

        /* Promise-based viewer-ready: all setActiveMedia calls queue here */
        this._viewerReadyPromise = new Promise((resolve) => {
          this._resolveViewerReady = resolve;
        });
      }

      connectedCallback() {
        const { viewer, thumbnails } = this.elements;
        const signal = this._controller.signal;

        this.stickyHeader = document.querySelector('sticky-header');

        /* Resolve viewer-ready */
        const viewerTag = viewer ? viewer.tagName.toLowerCase() : '';
        const readyPromise = viewerTag.includes('-')
          ? customElements.whenDefined(viewerTag)
          : Promise.resolve();

        readyPromise.then(() => {
          this._resolveViewerReady();
          if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
            this.removeListSemantic();
          }
        });

        if (!thumbnails) return;

        viewer.addEventListener(
          'slideChanged',
          this._debounce(this.onSlideChanged.bind(this), 30),
          { signal }
        );

        thumbnails.querySelectorAll('[data-target]').forEach((item) => {
          item.querySelector('button')?.addEventListener(
            'click',
            this.setActiveMedia.bind(this, item.dataset.target, false),
            { signal }
          );
        });
      }

      disconnectedCallback() {
        this._controller.abort();
      }

      /* ── Inline debounce ─────────────────────────────────── */
      _debounce(fn, wait) {
        let timer;
        return function(...args) {
          clearTimeout(timer);
          timer = setTimeout(() => fn.apply(this, args), wait);
        };
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        if (thumbnail) this.setActiveThumbnail(thumbnail);
      }

      /* ── setActiveMedia: always awaits viewer-ready ──────── */
      setActiveMedia(mediaId, prepend) {
        this._viewerReadyPromise.then(() => {
          this._setActiveMediaNow(mediaId, prepend);
        });
      }

      _setActiveMediaNow(mediaId, prepend) {
        const viewer = this.elements.viewer;
        if (!viewer) return;

        const activeMedia =
          viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          viewer.querySelector('[data-media-id]');

        if (!activeMedia) return;

        const previousActive = viewer.querySelector('[data-media-id].is-active');
        if (previousActive && previousActive !== activeMedia) {
          previousActive.classList.remove('is-active');
        }
        activeMedia.classList.add('is-active');

        if (prepend) {
          if (activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );
            if (activeThumbnail?.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          viewer.slider?.resetPages?.();
        }

        this.preventStickyHeader();

        requestAnimationFrame(() => {
          /*
           * FIX: Only scroll gallery into view on mobile OR when
           * thumbnails are present. Removed the window.scrollTo call
           * that was fighting product-info.js's scroll restore and
           * causing the page to jump on every swatch click.
           */
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
          }
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button[aria-current]')
          .forEach((btn) => btn.removeAttribute('aria-current'));

        thumbnail.querySelector('button')?.setAttribute('aria-current', 'true');

        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;

        const slider = this.elements.thumbnails.slider || this.elements.thumbnails;
        const sliderRect    = slider.getBoundingClientRect();
        const thumbnailRect = thumbnail.getBoundingClientRect();
        const targetLeft    = slider.scrollLeft + (thumbnailRect.left - sliderRect.left);

        slider.scrollTo({ left: targetLeft, behavior: 'smooth' });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;

        const announce = () => {
          if (!this.elements.liveRegion) return;
          this.elements.liveRegion.setAttribute('aria-hidden', 'false');
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings?.imageAvailable?.replace('[index]', position) || '';
          setTimeout(() => {
            this.elements.liveRegion?.setAttribute('aria-hidden', 'true');
          }, 2000);
        };

        if (image.complete) {
          announce();
        } else {
          image.onload = announce;
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia?.();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems?.forEach((slide) =>
          slide.setAttribute('role', 'presentation')
        );
      }
    }
  );
}